import React, { useState, useEffect } from 'react';
import {
  Card, Table, Tag, Space, Typography, Statistic, Row, Col,
  Button, Modal, Spin, Empty, Popconfirm, message, Tooltip, Grid
} from 'antd';
import {
  TrophyOutlined, FireOutlined, CalendarOutlined,
  EyeOutlined, DeleteOutlined, HistoryOutlined, HeartFilled, CommentOutlined
} from '@ant-design/icons';
import { getSessions, deleteSession, getFramesBySession } from '../../api/services/equalOffenseService';

const { Text } = Typography;
const { useBreakpoint } = Grid;

/**
 * Simple Sparkline Component - SVG-based mini chart
 */
const Sparkline = ({ data, width = 100, height = 24, color = '#1890ff', showLabels = false }) => {
  if (!data || data.length === 0) return <Text type="secondary">-</Text>;

  const max = Math.max(...data, 1);
  const min = 0;
  const range = max - min || 1;

  // Bei Labels brauchen wir mehr Platz oben
  const topPadding = showLabels ? 16 : 2;
  const chartHeight = height - topPadding;

  const points = data.map((value, index) => {
    const x = 10 + (index / (data.length - 1 || 1)) * (width - 20);
    const y = topPadding + chartHeight - ((value - min) / range) * (chartHeight - 4) - 2;
    return `${x},${y}`;
  }).join(' ');

  // Finde High Run Index
  const highRunIndex = data.indexOf(Math.max(...data));

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      {/* Linie */}
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Punkte und Labels */}
      {data.map((value, index) => {
        const x = 10 + (index / (data.length - 1 || 1)) * (width - 20);
        const y = topPadding + chartHeight - ((value - min) / range) * (chartHeight - 4) - 2;
        const isHighRun = index === highRunIndex && value > 0;
        return (
          <g key={index}>
            <circle
              cx={x}
              cy={y}
              r={isHighRun ? 4 : 3}
              fill={isHighRun ? '#ff4d4f' : color}
            />
            {showLabels && (
              <text
                x={x}
                y={y - 8}
                textAnchor="middle"
                fontSize={11}
                fontWeight={isHighRun ? 'bold' : 'normal'}
                fill={isHighRun ? '#ff4d4f' : '#666'}
              >
                {value}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

/**
 * EqualOffenseHistory Component
 * Displays history of all Equal Offense sessions
 */
const EqualOffenseHistory = ({ playerId }) => {
  // Responsive breakpoints
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionFrames, setSessionFrames] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  // Cache für Frame-Daten (für Sparklines)
  const [frameCache, setFrameCache] = useState({});

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const data = await getSessions();
      // Nur beendete Sessions anzeigen
      const finishedSessions = data.filter(s => s.is_finished);
      setSessions(finishedSessions);

      // Lade Frames für alle Sessions (für Sparklines)
      const cache = {};
      await Promise.all(
        finishedSessions.map(async (session) => {
          try {
            const frames = await getFramesBySession(session.id);
            cache[session.id] = frames;
          } catch (e) {
            cache[session.id] = [];
          }
        })
      );
      setFrameCache(cache);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (session) => {
    setSelectedSession(session);
    setDetailModalVisible(true);
    setDetailLoading(true);
    try {
      // Nutze Cache wenn vorhanden
      if (frameCache[session.id]) {
        setSessionFrames(frameCache[session.id]);
        setDetailLoading(false);
      } else {
        const frames = await getFramesBySession(session.id);
        setSessionFrames(frames);
      }
    } catch (error) {
      console.error('Failed to load frames:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await deleteSession(sessionId);
      message.success('Session gelöscht');
      loadSessions();
    } catch (error) {
      message.error('Fehler beim Löschen');
    }
  };

  const getStufeColor = (stufe) => {
    if (stufe <= 3) return 'green';
    if (stufe <= 6) return 'orange';
    return 'red';
  };

  // Anzahl Leben für Stufe
  const getLivesForStufe = (stufe) => {
    const livesMap = { 1: 3, 2: 2, 3: 1, 4: 3, 5: 2, 6: 1, 7: 1 };
    return livesMap[stufe] || 1;
  };

  const columns = [
    {
      title: 'Datum',
      dataIndex: 'date',
      key: 'date',
      width: 140,
      render: (date) => (
        <Space>
          <CalendarOutlined />
          {new Date(date).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
          })}
        </Space>
      ),
      sorter: (a, b) => new Date(b.date) - new Date(a.date),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Stufe',
      dataIndex: 'stufe',
      key: 'stufe',
      width: 80,
      render: (stufe) => (
        <Tag color={getStufeColor(stufe)}>S{stufe}</Tag>
      ),
      filters: [
        { text: 'Stufe 1', value: 1 },
        { text: 'Stufe 2', value: 2 },
        { text: 'Stufe 3', value: 3 },
        { text: 'Stufe 4', value: 4 },
        { text: 'Stufe 5', value: 5 },
        { text: 'Stufe 6', value: 6 },
        { text: 'Stufe 7', value: 7 },
      ],
      onFilter: (value, record) => record.stufe === value,
    },
    {
      title: 'Punkte',
      dataIndex: 'total_points',
      key: 'total_points',
      width: 70,
      render: (points) => (
        <Text strong style={{ color: '#1890ff', fontSize: 16 }}>
          {points}
        </Text>
      ),
      sorter: (a, b) => a.total_points - b.total_points,
    },
    {
      title: 'HR',
      dataIndex: 'high_run',
      key: 'high_run',
      width: 50,
      render: (hr) => (
        <Tooltip title="High Run">
          <Text style={{ color: '#ff4d4f' }}>{hr}</Text>
        </Tooltip>
      ),
      sorter: (a, b) => a.high_run - b.high_run,
    },
    {
      title: 'Ø',
      key: 'average',
      width: 50,
      render: (_, record) => (
        record.total_innings > 0
          ? (record.total_points / record.total_innings).toFixed(1)
          : '-'
      ),
    },
    {
      title: 'Verlauf',
      key: 'sparkline',
      width: 120,
      render: (_, record) => {
        const frames = frameCache[record.id] || [];
        const pointsData = frames.map(f => f.points);
        return (
          <Tooltip title={pointsData.join(' → ')}>
            <Sparkline data={pointsData} width={100} height={24} />
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          />
          <Popconfirm
            title="Session löschen?"
            onConfirm={() => handleDeleteSession(record.id)}
            okText="Ja"
            cancelText="Nein"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Statistiken berechnen
  const stats = {
    totalSessions: sessions.length,
    totalPoints: sessions.reduce((sum, s) => sum + s.total_points, 0),
    bestSession: sessions.length > 0 ? Math.max(...sessions.map(s => s.total_points)) : 0,
    bestHighRun: sessions.length > 0 ? Math.max(...sessions.map(s => s.high_run)) : 0,
    avgPerSession: sessions.length > 0
      ? (sessions.reduce((sum, s) => sum + s.total_points, 0) / sessions.length).toFixed(1)
      : 0,
  };

  return (
    <div>
      {/* Statistik-Übersicht */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title="Sessions"
              value={stats.totalSessions}
              prefix={<HistoryOutlined />}
            />
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Statistic
              title="Gesamt Punkte"
              value={stats.totalPoints}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Statistic
              title="Beste Session"
              value={stats.bestSession}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Statistic
              title="Bester High Run"
              value={stats.bestHighRun}
              prefix={<FireOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Statistic
              title="Ø pro Session"
              value={stats.avgPerSession}
            />
          </Col>
        </Row>
      </Card>

      {/* Sessions-Tabelle */}
      <Card title="Vergangene Sessions">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin size="large" />
          </div>
        ) : sessions.length === 0 ? (
          <Empty description="Noch keine abgeschlossenen Sessions" />
        ) : (
          <Table
            dataSource={sessions}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            size="small"
            scroll={{ x: 600 }}
          />
        )}
      </Card>

      {/* Detail-Modal */}
      <Modal
        title={
          <Space size={isMobile ? 4 : 8}>
            <TrophyOutlined />
            <span style={{ fontSize: isMobile ? 14 : 16 }}>Session Details</span>
            {selectedSession && (
              <Tag color={getStufeColor(selectedSession.stufe)} style={{ fontSize: isMobile ? 11 : 12 }}>
                Stufe {selectedSession.stufe}
              </Tag>
            )}
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 800}
        style={isMobile ? { top: 20, maxWidth: '100%', margin: '0 8px' } : undefined}
        styles={{ body: { padding: isMobile ? 12 : 24 } }}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: isMobile ? 30 : 50 }}>
            <Spin />
          </div>
        ) : selectedSession && (
          <div>
            {/* Session-Info - kompakt auf Mobile */}
            {isMobile ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                <div style={{ flex: '1 1 45%', textAlign: 'center', padding: '4px 0' }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Gesamt</Text>
                  <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1890ff' }}>
                    {selectedSession.total_points}
                  </div>
                </div>
                <div style={{ flex: '1 1 45%', textAlign: 'center', padding: '4px 0' }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>High Run</Text>
                  <div style={{ fontSize: 18, fontWeight: 'bold', color: '#ff4d4f' }}>
                    {selectedSession.high_run}
                  </div>
                </div>
                <div style={{ flex: '1 1 45%', textAlign: 'center', padding: '4px 0' }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Durchschnitt</Text>
                  <div style={{ fontSize: 18, fontWeight: 'bold' }}>
                    {selectedSession.total_innings > 0
                      ? (selectedSession.total_points / selectedSession.total_innings).toFixed(1)
                      : 0}
                  </div>
                </div>
                <div style={{ flex: '1 1 45%', textAlign: 'center', padding: '4px 0' }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>Aufnahmen</Text>
                  <div style={{ fontSize: 18, fontWeight: 'bold' }}>
                    {selectedSession.total_innings}
                  </div>
                </div>
              </div>
            ) : (
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                  <Statistic title="Gesamtpunkte" value={selectedSession.total_points} />
                </Col>
                <Col span={6}>
                  <Statistic title="High Run" value={selectedSession.high_run} />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Durchschnitt"
                    value={selectedSession.total_innings > 0
                      ? (selectedSession.total_points / selectedSession.total_innings).toFixed(1)
                      : 0}
                  />
                </Col>
                <Col span={6}>
                  <Statistic title="Aufnahmen" value={selectedSession.total_innings} />
                </Col>
              </Row>
            )}

            {/* Session-Notizen */}
            {selectedSession.notes && (
              <Card size="small" style={{ marginBottom: isMobile ? 8 : 16, background: '#f6ffed' }}>
                <Space>
                  <CommentOutlined style={{ color: '#52c41a' }} />
                  <Text style={{ fontSize: isMobile ? 12 : 14 }}>{selectedSession.notes}</Text>
                </Space>
              </Card>
            )}

            {/* Sparkline groß mit Labels */}
            <Card size="small" style={{ marginBottom: isMobile ? 8 : 16 }} bodyStyle={{ padding: isMobile ? 8 : 12 }}>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: isMobile ? 11 : 14 }}>Punkteverlauf</Text>
                <div style={{ marginTop: 8, overflowX: 'auto' }}>
                  <Sparkline
                    data={sessionFrames.map(f => f.points)}
                    width={isMobile ? Math.max(280, sessionFrames.length * 35) : Math.max(700, sessionFrames.length * 50)}
                    height={isMobile ? 50 : 80}
                    color="#1890ff"
                    showLabels={true}
                  />
                </div>
              </div>
            </Card>

            {/* Aufnahmen-Liste - Mobile: Kompakte Liste, Desktop: Tabelle */}
            {isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {sessionFrames.map((frame, idx) => {
                  const runningTotal = sessionFrames
                    .slice(0, idx + 1)
                    .reduce((sum, f) => sum + f.points, 0);
                  const lifePoints = frame.life_points || [];
                  const lifeFouls = frame.life_fouls || [];
                  const foulCount = lifeFouls.filter(f => f).length;
                  const isHighRun = frame.points === selectedSession.high_run && frame.points > 0;

                  return (
                    <div
                      key={frame.id}
                      style={{
                        padding: '8px 10px',
                        background: '#fafafa',
                        borderRadius: 6,
                        borderLeft: isHighRun ? '3px solid #ff4d4f' : '3px solid transparent',
                      }}
                    >
                      {/* Zeile 1: #, Punkte, Σ */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <Tag color="default" style={{ margin: 0, fontSize: 11 }}>#{frame.frame_number}</Tag>
                        <Text strong style={{
                          fontSize: isHighRun ? 16 : 14,
                          color: isHighRun ? '#ff4d4f' : undefined
                        }}>
                          {frame.points}
                          {isHighRun && <FireOutlined style={{ marginLeft: 4 }} />}
                        </Text>
                        <Tag color="green" style={{ margin: 0, marginLeft: 'auto', fontSize: 11 }}>Σ{runningTotal}</Tag>
                      </div>
                      {/* Zeile 2: Leben-Verteilung */}
                      {lifePoints.length > 0 && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
                          {lifePoints.map((points, lifeIdx) => {
                            const hasFoul = lifeFouls[lifeIdx] || false;
                            return (
                              <Tag
                                key={lifeIdx}
                                color={hasFoul ? 'orange' : (points > 0 ? 'blue' : 'default')}
                                style={{ margin: 0, fontSize: 10, padding: '0 4px' }}
                              >
                                <HeartFilled style={{ color: '#ff4d4f', marginRight: 2, fontSize: 9 }} />
                                {points}
                                {hasFoul && <span style={{ marginLeft: 2 }}>F</span>}
                              </Tag>
                            );
                          })}
                        </div>
                      )}
                      {/* Zeile 3: Status */}
                      {(frame.lives_used > 0 || foulCount > 0 || frame.notes) && (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          {frame.lives_used > 0 && (
                            <Tag color="red" style={{ margin: 0, fontSize: 10 }}>-{frame.lives_used} Leben</Tag>
                          )}
                          {foulCount > 0 && (
                            <Tag color="orange" style={{ margin: 0, fontSize: 10 }}>{foulCount}x Foul</Tag>
                          )}
                          {frame.notes && (
                            <Tooltip title={frame.notes}>
                              <CommentOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                            </Tooltip>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <Table
                dataSource={sessionFrames.map((frame, idx) => {
                  const runningTotal = sessionFrames
                    .slice(0, idx + 1)
                    .reduce((sum, f) => sum + f.points, 0);
                  return { ...frame, runningTotal, key: frame.id };
                })}
                columns={[
                  {
                    title: '#',
                    dataIndex: 'frame_number',
                    key: 'frame_number',
                    width: 40,
                  },
                  {
                    title: 'Pkt',
                    dataIndex: 'points',
                    key: 'points',
                    width: 50,
                    render: (p, record) => (
                      <Text
                        strong
                        style={{
                          color: p === selectedSession.high_run && p > 0 ? '#ff4d4f' : undefined,
                          fontSize: p === selectedSession.high_run && p > 0 ? 16 : undefined,
                        }}
                      >
                        {p}
                        {p === selectedSession.high_run && p > 0 && (
                          <FireOutlined style={{ marginLeft: 4, color: '#ff4d4f' }} />
                        )}
                      </Text>
                    ),
                  },
                  {
                    title: <span><HeartFilled style={{ color: '#ff4d4f', fontSize: 12 }} />-Vert.</span>,
                    key: 'life_points',
                    render: (_, record) => {
                      const lifePoints = record.life_points || [];
                      const lifeFouls = record.life_fouls || [];
                      if (lifePoints.length === 0) {
                        return <Text type="secondary">-</Text>;
                      }
                      return (
                        <Space size={2}>
                          {lifePoints.map((points, idx) => {
                            const hasFoul = lifeFouls[idx] || false;
                            return (
                              <Tag
                                key={idx}
                                color={hasFoul ? 'orange' : (points > 0 ? 'blue' : 'default')}
                                style={{ margin: 0, padding: '0 4px', fontSize: 11 }}
                              >
                                <HeartFilled style={{ color: '#ff4d4f', marginRight: 2, fontSize: 9 }} />
                                {points}
                                {hasFoul && <span style={{ marginLeft: 1, fontSize: 9 }}>F</span>}
                              </Tag>
                            );
                          })}
                        </Space>
                      );
                    },
                  },
                  {
                    title: 'Σ',
                    dataIndex: 'runningTotal',
                    key: 'runningTotal',
                    width: 45,
                    render: (t) => <Tag color="green" style={{ margin: 0, fontSize: 11 }}>{t}</Tag>,
                  },
                  {
                    title: 'Info',
                    key: 'status',
                    width: 80,
                    render: (_, record) => {
                      const lifeFouls = record.life_fouls || [];
                      const foulCount = lifeFouls.filter(f => f).length;
                      return (
                        <Space size={2}>
                          {record.lives_used > 0 && (
                            <Tag color="red" style={{ margin: 0, padding: '0 4px', fontSize: 10 }}>
                              -{record.lives_used}<HeartFilled style={{ marginLeft: 2, fontSize: 9 }} />
                            </Tag>
                          )}
                          {foulCount > 0 && (
                            <Tag color="orange" style={{ margin: 0, padding: '0 4px', fontSize: 10 }}>
                              {foulCount}F
                            </Tag>
                          )}
                          {record.notes && (
                            <Tooltip title={record.notes}>
                              <CommentOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                            </Tooltip>
                          )}
                        </Space>
                      );
                    },
                  },
                ]}
                pagination={false}
                size="small"
                scroll={{ x: 350 }}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EqualOffenseHistory;
